import React, { createContext, useContext, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigProvider } from 'antd'
import enUS from 'antd/locale/en_US'
import zhTW from 'antd/locale/zh_TW'

interface LanguageContextType {
  language: string
  changeLanguage: (lang: string) => void
  t: (key: string, options?: any) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation()

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
  }

  // Get Ant Design locale based on current language
  const getAntdLocale = () => {
    switch (i18n.language) {
      case 'zh':
      case 'zh-TW':
        return zhTW
      case 'en':
      default:
        return enUS
    }
  }

  const value: LanguageContextType = {
    language: i18n.language,
    changeLanguage,
    t,
  }

  return (
    <LanguageContext.Provider value={value}>
      <ConfigProvider locale={getAntdLocale()}>
        {children}
      </ConfigProvider>
    </LanguageContext.Provider>
  )
} 